// ═══════════════════════════════════════════════════════════════════════════
// Eventos de venta — Helper puro
// ───────────────────────────────────────────────────────────────────────────
// Funciones para detectar y manejar eventos de calendario comercial (Hot
// Sale, Cyber Monday, Día de la Madre, etc.) que afectan las series de
// ventas y por lo tanto el análisis de tendencia.
//
// Modelo:
//   - Eventos a nivel plataforma (catalogoEventosCalendario) → aplican a
//     TODAS las empresas del país. Por default, todos vigentes se incluyen.
//   - Eventos custom por empresa (empresas/{id}/eventosVenta) → suman a los
//     plataforma. Sirve para lanzamientos, paros, promociones propias.
//
// Cada evento tiene un campo `postEventoDias` que define cuántos días
// después del fin se consideran "cola del evento" (caída por inventario
// quemado, expectativa saturada). Esos días también se excluyen del cálculo
// de tendencia base.
// ═══════════════════════════════════════════════════════════════════════════

// Tipos de evento conocidos. El frontend muestra estos labels y permite
// agregar 'otro' para casos no tipificados.
export const TIPOS_EVENTO = Object.freeze({
  hot_sale:        { label: 'Hot Sale',        emoji: '🔥' },
  cyber_monday:    { label: 'Cyber Monday',    emoji: '💻' },
  black_friday:    { label: 'Black Friday',    emoji: '🛍️' },
  navidad:         { label: 'Navidad',         emoji: '🎄' },
  reyes:           { label: 'Reyes',           emoji: '👑' },
  dia_madre:       { label: 'Día de la Madre', emoji: '💐' },
  dia_padre:       { label: 'Día del Padre',   emoji: '👔' },
  dia_nino:        { label: 'Día del Niño',    emoji: '🧸' },
  cuotas_simples:  { label: 'Cuotas Simples',  emoji: '💳' },
  san_valentin:    { label: 'San Valentín',    emoji: '❤️' },
  // Eventos custom de empresa
  lanzamiento:     { label: 'Lanzamiento',     emoji: '🚀' },
  promocion:       { label: 'Promoción propia', emoji: '🏷️' },
  paro:            { label: 'Paro / Corte',    emoji: '⛔' },
  cierre_seller:   { label: 'Cierre seller',   emoji: '🚫' },
  otro:            { label: 'Otro',            emoji: '📅' },
})

// Color común para todos los eventos en el gráfico (banda violeta translúcida)
export const COLOR_EVENTO = '#8B5CF6'  // violet-500

function _toMs(d) {
  if (!d) return null
  if (d instanceof Date) return d.getTime()
  if (typeof d === 'string') return new Date(d).getTime()
  if (d?.toDate) return d.toDate().getTime()
  if (typeof d === 'number') return d
  return null
}

// Día UTC sin hora (para comparar por día calendario)
function _diaInicio(ms) {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}
function _diaFin(ms) {
  const d = new Date(ms)
  d.setHours(23, 59, 59, 999)
  return d.getTime()
}

// Normaliza un evento crudo de Firestore a un shape predecible.
// rangos: { eventoMs: [desde, hasta], postEventoMs: [hasta+1, hasta+postDias] }
export function normalizarEvento(ev) {
  if (!ev) return null
  const desdeMs = _toMs(ev.desde)
  const hastaMs = _toMs(ev.hasta)
  if (desdeMs == null || hastaMs == null) return null

  const inicioEvento = _diaInicio(desdeMs)
  const finEvento = _diaFin(hastaMs)
  const postDias = Number(ev.postEventoDias || 0)
  const inicioPost = postDias > 0 ? finEvento + 1 : null
  const finPost = postDias > 0 ? _diaFin(finEvento + postDias * 24 * 60 * 60 * 1000) : null

  return {
    id: ev.id,
    nombre: ev.nombre || 'Evento sin nombre',
    tipo: ev.tipo || 'otro',
    paisId: ev.paisId || null,
    origen: ev.origen || (ev.empresaId ? 'empresa' : 'plataforma'),
    desde: new Date(inicioEvento),
    hasta: new Date(finEvento),
    postEventoDias: postDias,
    postDesde: inicioPost ? new Date(inicioPost) : null,
    postHasta: finPost ? new Date(finPost) : null,
    inicioEvento,
    finEvento,
    inicioPost,
    finPost,
    intensidadHistorica: ev.intensidadHistorica || null,
    notas: ev.notas || null,
    vigente: ev.vigente !== false,
    // ─── Campos para integración con promociones ML ───
    aceleradorEstimado: typeof ev.aceleradorEstimado === 'number' && isFinite(ev.aceleradorEstimado)
      ? ev.aceleradorEstimado
      : null,
    keywordsMatching: Array.isArray(ev.keywordsMatching) ? ev.keywordsMatching : [],
    fuente: ev.fuente || 'manual',           // 'manual' | 'ml-auto'
    customizado: ev.customizado === true,
    promotionIdsML: Array.isArray(ev.promotionIdsML) ? ev.promotionIdsML : [],
  }
}

// ─── Estado derivado de fechas ─────────────────────────────────────────────
//
// No se persiste — se calcula al vuelo desde desde/hasta y vigente.
// Uso en la UI del ABM y en el badge de cada evento.
//
//   programado → start > hoy
//   activo     → start ≤ hoy ≤ finPost (incluye cola post-evento)
//   finalizado → finPost < hoy
//   cancelado  → vigente === false (override manual del admin)

export function estadoEvento(eventoNormalizado, ahora = Date.now()) {
  if (!eventoNormalizado) return null
  if (eventoNormalizado.vigente === false) return 'cancelado'
  const desdeMs = eventoNormalizado.inicioEvento
  const hastaConPost = eventoNormalizado.finPost ?? eventoNormalizado.finEvento
  if (ahora < desdeMs) return 'programado'
  if (ahora > hastaConPost) return 'finalizado'
  return 'activo'
}

// ─── Lift de una campaña ML usando el calendario comercial ─────────────────
//
// Cascada de resolución:
//   1) Match contra calendario por `keywordsMatching` (case-insensitive,
//      contiene) → usar `aceleradorEstimado` del evento matcheado.
//   2) Match por `tipo` del evento (hot_sale, cyber_monday, etc.) si el name
//      ML lo contiene → usar acelerador del evento.
//   3) Fallback por `type` ML (DEAL, MARKETPLACE_CAMPAIGN, etc.) en config.
//   4) Default 1.0 (sin acelerador).
//
// Devuelve { lift, fuente, eventoId, detalle } para trazabilidad en UI.

const TIPO_ML_DEFAULT = {
  MARKETPLACE_CAMPAIGN:    1.7,
  SMART:                   1.2,
  PRICE_MATCHING:          1.2,
  PRICE_MATCHING_MELI_ALL: 1.4,
  DEAL:                    1.3,
  DOD:                     2.0,
  LIGHTNING:               2.2,
  UNHEALTHY_STOCK:         2.0,
  SELLER_CAMPAIGN:         1.2,
  SELLER_COUPON_CAMPAIGN:  1.3,
  VOLUME:                  1.4,
  PRE_NEGOTIATED:          1.3,
}

export function resolverLiftCampaña({ campañaML, eventos = [], aceleradorPorTipo = TIPO_ML_DEFAULT } = {}) {
  if (!campañaML) return { lift: 1.0, fuente: 'default', detalle: 'sin campaña' }
  const nombreUpper = String(campañaML.name || campañaML.nombre || '').toUpperCase()
  // 1) Match por keywords del calendario (incluye match por tipo del evento)
  for (const ev of eventos) {
    if (ev?.vigente === false) continue
    const kws = Array.isArray(ev.keywordsMatching) ? ev.keywordsMatching : []
    const match = kws.find(k => k && nombreUpper.includes(String(k).toUpperCase()))
    if (match && typeof ev.aceleradorEstimado === 'number' && isFinite(ev.aceleradorEstimado)) {
      return {
        lift: ev.aceleradorEstimado,
        fuente: 'evento',
        eventoId: ev.id,
        detalle: `Evento "${ev.nombre}" matchea por keyword "${match}"`,
      }
    }
  }
  // 2) Fallback por type ML
  const type = campañaML.type || campañaML.promotionType
  if (type && aceleradorPorTipo[type] != null) {
    return {
      lift: aceleradorPorTipo[type],
      fuente: 'tipo',
      detalle: `Acelerador genérico para tipo ${type}`,
    }
  }
  // 3) Default
  return { lift: 1.0, fuente: 'default', detalle: 'Sin info — default conservador' }
}

// Combina eventos plataforma + empresa en un array unificado, normalizados,
// ordenados por fecha de inicio. Eventos no vigentes se descartan.
export function combinarEventos(plataforma = [], empresa = []) {
  const todos = []
  for (const e of plataforma) {
    const n = normalizarEvento({ ...e, origen: 'plataforma' })
    if (n && n.vigente) todos.push(n)
  }
  for (const e of empresa) {
    const n = normalizarEvento({ ...e, origen: 'empresa' })
    if (n && n.vigente) todos.push(n)
  }
  todos.sort((a, b) => a.inicioEvento - b.inicioEvento)
  return todos
}

// Dado un timestamp en ms, devuelve el evento que lo contiene (en evento o
// post-evento). Si está en varios, prioriza el primero (los rangos solapados
// se tratan como mismo período no-baseline).
//   → { evento, fase: 'evento' | 'post' } o null
export function detectarEvento(fechaMs, eventosNorm) {
  if (!fechaMs || !Array.isArray(eventosNorm)) return null
  for (const e of eventosNorm) {
    if (fechaMs >= e.inicioEvento && fechaMs <= e.finEvento) {
      return { evento: e, fase: 'evento' }
    }
    if (e.inicioPost && fechaMs >= e.inicioPost && fechaMs <= e.finPost) {
      return { evento: e, fase: 'post' }
    }
  }
  return null
}

// Dada una serie de puntos diarios { fecha, ... }, devuelve la misma serie
// pero filtrada — sin los días que están dentro de evento o post-evento.
// Útil para alimentar la regresión "base" (tendencia natural).
export function excluirDiasEventos(data, eventosNorm) {
  if (!Array.isArray(data)) return []
  if (!Array.isArray(eventosNorm) || eventosNorm.length === 0) return data
  return data.filter(d => {
    const ms = _toMs(d.fecha) || _toMs(d.fechaMs)
    if (ms == null) return true
    return detectarEvento(ms, eventosNorm) == null
  })
}

// Calcula el "over" (lift) de un evento sobre la baseline diaria.
// Devuelve % positivo si el evento generó MÁS que el promedio sin eventos.
//   over = (promedioEvento − baseline) / baseline
// También devuelve el total acumulado durante los días de evento (no post).
export function calcularOverEvento(evento, data, baselinePromedio, key = 'ventas') {
  if (!evento || !Array.isArray(data) || data.length === 0) return null
  const diasEvento = data.filter(d => {
    const ms = _toMs(d.fecha) || _toMs(d.fechaMs)
    if (ms == null) return false
    return ms >= evento.inicioEvento && ms <= evento.finEvento
  })
  if (diasEvento.length === 0) return null

  const valores = diasEvento.map(d => Number(d[key]) || 0)
  const totalEvento = valores.reduce((a, b) => a + b, 0)
  const promedioEvento = totalEvento / valores.length

  const baseline = Number(baselinePromedio) || 0
  const overPct = baseline > 0 ? (promedioEvento - baseline) / baseline : null
  const overFactor = baseline > 0 ? promedioEvento / baseline : null

  return {
    eventoId: evento.id,
    nombre: evento.nombre,
    tipo: evento.tipo,
    diasIncluidos: valores.length,
    totalEvento,
    promedioEvento,
    baseline,
    overPct,           // ej. +3.2 = +320% sobre la baseline
    overFactor,        // ej. 4.2 = vendió 4.2× más que un día normal
  }
}

// Formatea un rango de fechas para mostrar en UI: "11-15 may"
export function formatRangoEvento(evento) {
  if (!evento) return ''
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  const desde = evento.desde instanceof Date ? evento.desde : new Date(evento.inicioEvento)
  const hasta = evento.hasta instanceof Date ? evento.hasta : new Date(evento.finEvento)
  const mismoMes = desde.getMonth() === hasta.getMonth() && desde.getFullYear() === hasta.getFullYear()
  if (mismoMes) {
    return `${desde.getDate()}-${hasta.getDate()} ${meses[desde.getMonth()]}`
  }
  return `${desde.getDate()} ${meses[desde.getMonth()]} – ${hasta.getDate()} ${meses[hasta.getMonth()]}`
}
